import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { object, string, bool, ref } from 'yup';
import get from 'lodash/get';
import { Row, Col, Alert } from 'react-bootstrap';
import { useIntl, IntlProvider } from 'react-intl';
import Translation from '../../../../../components/stateless/Translation';
import withForm from '../../../../../../admin/hocs/withForm';
import SMFormFieldToggle from '../../../../components/stateless/SMFormFieldToggle';
import CheckoutSubmitButton from '../CheckoutSubmitButton';
import CheckoutFormFieldText from '../CheckoutFormFieldText';
import CheckoutFormFieldCreditCard from '../CheckoutFormFieldCreditCard';
import CheckoutFormExpirationDate from '../CheckoutFormExpirationDate';
import {
  TRACK_CLICK_CHECKOUT_STEP_FIVE_SUBMIT,
  TRACK_CHECKOUT_ADD_PAYMENT_INFO,
  TRACK_CHECKOUT_STEP,
} from '../../../../../../src/web/utils/dataLayer';
import hourglass from './hourglass.svg';
import styles from './styles.scss';
import SMTooltip from '../../../../components/stateless/SMTooltip';
import {
  validateCreditCardNumber,
  validateCreditCardSecurityCode,
  validateExpireDate,
} from '../../StorageMartCheckoutTotalRecall/Payment/validation';
import ApplyPromotion from './ApplyPromotion';
import { paymentProviderAchTypeMapping } from '../../../../../../constants/paymentProvider';
import CheckoutFormDropdown from '../CheckoutFormDropdown';
import { GB, US, CA } from '../../../../../../src/shared/i18n';
import CheckoutFormStateDropdown from '../CheckoutFormStateDropdown';
import useFlipper from '../../../../../../modules/react/hooks/useFlipper';
import {
  FLIPPER_ACH_US,
  FLIPPER_ACH_CA,
  FLIPPER_ACH_GB,
} from '../../../../../../constants/flippers';

const propTypes = {
  formData: PropTypes.shape().isRequired,
  formError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  facility: PropTypes.shape().isRequired,
  errorMessage: PropTypes.shape().isRequired,
  receipt: PropTypes.shape({
    totalAmount: PropTypes.number.isRequired,
  }),
  checkPromoCode: PropTypes.func().isRequired,
  checkingPromoCode: PropTypes.bool.isRequired,
  checkPromoCodeData: PropTypes.shape({
    success: PropTypes.bool.isRequired,
    discountPlanId: PropTypes.string,
  }),
  appliedPromotion: PropTypes.shape(),
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }).isRequired,
};

const defaultProps = {
  receipt: undefined,
  checkPromoCodeData: undefined,
  appliedPromotion: undefined,
};

const PAYMENT_TYPES = {
  card: 'card',
  ach: 'ach',
};

const validationSchema = object().shape({
  creditCardNumber: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.card,
      then: string().required('widgets.checkout.creditCardNumber.required'),
    }),
  creditCardExpireMonth: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.card,
      then: string().required('widgets.checkout.creditCardExpireMonth.required'),
    }),
  creditCardExpireYear: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.card,
      then: string().required('widgets.checkout.creditCardExpireYear.required'),
    }),
  creditCardSecurityCode: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.card,
      then: string().required('widgets.checkout.creditCardSecurityCode.required'),
    }),
  // Bank account validation
  bankName: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.bankName.required'),
    }),
  accountType: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.accountType.required'),
    }),
  routingNumber: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.routingNumber.required'),
    }),
  accountNumber: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.accountNumber.required'),
    }),
  accountNumberConfirmation: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string()
        .oneOf([ref('accountNumber'), null], 'widgets.checkout.accountNumberConfirmation.mismatch')
        .required('widgets.checkout.accountNumberConfirmation.required'),
    }),
  agreeToACH: bool()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: bool()
        .required('widgets.checkout.agreeToACH.required')
        .oneOf([true], 'widgets.checkout.agreeToACH.required'),
    }),
  billingFirstName: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingFirstName.required'),
    }),
  billingLastName: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingLastName.required'),
    }),
  billingAddress1: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingAddress1.required'),
    }),
  billingAddress2: string().nullable(),
  billingCity: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingCity.required'),
    }),
  billingState: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingState.required'),
    }),
  billingPostal: string().nullable()
    .when('paymentType', {
      is: PAYMENT_TYPES.ach,
      then: string().required('widgets.checkout.billingPostal.required'),
    }),
  autopayEnroll: bool(),
  paymentType: string()
    .required('widgets.checkout.paymentType.required'),
  payOption: string(),

});

const initialValues = {
  creditCardNumber: '',
  creditCardExpireMonth: '',
  creditCardExpireYear: '',
  creditCardSecurityCode: '',
  // Bank account fields
  bankName: '',
  accountType: '',
  routingNumber: '',
  accountNumber: '',
  accountNumberConfirmation: '',
  agreeToACH: undefined,
  autopayEnroll: false,
  paymentType: '',
  payOption: 'payNow',
};

const today = new Date();

const FORM_SUBMIT_TYPES = {
  payment: 'payment',
  promo: 'promo',
};

const PaymentForm = ({
  submitForm,
  submitting,
  formData,
  formError,
  values,
  facility,
  receipt,
  setFieldValue,
  checkPromoCode,
  checkingPromoCode,
  checkPromoCodeData,
  appliedPromotion,
  removeAppliedPromotion,
  handleChange,
}) => {
  const intl = useIntl();
  const [hasTrackedDataLayer, setHasTrackedDataLayer] = useState(false);
  const [useExistingBillingAddress, setUseExistingBillingAddress] = useState(true);
  const {
    paymentProvider,
  } = facility;
  const isFlipperACHUSEnabled = useFlipper(FLIPPER_ACH_US);
  const isFlipperACHCAEnabled = useFlipper(FLIPPER_ACH_CA);
  const isFlipperACHGBEnabled = useFlipper(FLIPPER_ACH_GB);
  const hasACHPaymentProvider = (
    ((get(facility, 'address.country') === US && isFlipperACHUSEnabled)
    || (get(facility, 'address.country') === CA && isFlipperACHCAEnabled)
    || (get(facility, 'address.country') === GB && isFlipperACHGBEnabled))
    && paymentProvider !== ''
    && paymentProviderAchTypeMapping[paymentProvider]
  );

  useEffect(() => {
    if (!hasACHPaymentProvider) {
      setFieldValue('paymentType', PAYMENT_TYPES.card);
    }
  }, [paymentProvider]);

  useEffect(() => {
    if (!hasTrackedDataLayer && facility) {
      setHasTrackedDataLayer(true);
      TRACK_CHECKOUT_STEP(
        get(facility, 'unitGroup'),
        facility,
        'new_booking_flow_step4',
        'payment',
      );
    }
  }, [facility, hasTrackedDataLayer]);

  const payNowClick = rentalType => (
    TRACK_CHECKOUT_ADD_PAYMENT_INFO(
      get(facility, 'unitGroup'),
      facility,
      get(receipt, 'totalAmount'),
      intl,
      rentalType,
    )
  );

  const tooltipCCVTitle = `widgets.checkout.ccvTooltipBody.${values.creditCardNumber.startsWith('3') ? 'amex' : 'default'}`;
  const tooltipCCVTitleText = `Your security code is a ${values.creditCardNumber.startsWith('3')
    ? 'four digit number above the credit card number'
    : 'three digit number on the back of your card'}.`;

  const handleFormSubmit = (event) => {
    event.preventDefault();
    const paymentFormData = new FormData(event.target);
    const formValues = Object.fromEntries(paymentFormData.entries());

    if (event.nativeEvent.submitter.value === FORM_SUBMIT_TYPES.payment) {
      submitForm();
    } else if (event.nativeEvent.submitter.value === FORM_SUBMIT_TYPES.promo) {
      checkPromoCode(
        facility.id,
        facility.unitGroup.id,
        formValues.promoCode,
      );
    }
  };

  const useDifferentBillingAddress = () => {
    setFieldValue('billingFirstName', '');
    setFieldValue('billingLastName', '');
    setFieldValue('billingAddress1', '');
    setFieldValue('billingAddress2', '');
    setFieldValue('billingCity', '');
    setFieldValue('billingState', '');
    setFieldValue('billingPostal', '');
    setUseExistingBillingAddress(false);
  };

  return (
    <form onSubmit={handleFormSubmit}>
      {
        formError ? (
          <Alert bsStyle="danger">
            <Translation
              id="widgets.checkout.formError"
              defaultMessage="An error was encountered when submitting the form. Please try again."
            />
          </Alert>
        ) : null
      }
      {/* Payment Method Selection */}
      <Row>
        <Col xs={12}>
          <h4 className="mb-4 text-lg font-semibold">
            <Translation
              id="widgets.checkout.paymentMethodTitle"
              defaultMessage="Payment Method"
            />
          </h4>
          <div className="overflow-hidden mb-3 rounded-lg border border-gray-300">
            <div className="flex flex-col">
              {hasACHPaymentProvider ? (
                <div>
                  <div className="p-4 mb-0">
                    <SMFormFieldToggle
                      type="radio"
                      name="paymentType"
                      value={PAYMENT_TYPES.ach}
                      label={(
                        <Translation
                          id="widgets.checkout.bankAccount"
                          defaultMessage="Bank Account"
                        />
                      )}
                      className="!mb-0"
                    />
                  </div>
                  {/* Bank Account Form */}
                  {values.paymentType === PAYMENT_TYPES.ach && (
                    <div className="px-4 py-2">
                      <Row>
                        <Col xs={12} md={8}>
                          <CheckoutFormFieldText
                            requiredStar
                            name="bankName"
                            label={{
                              defaultMessage: 'Bank Name',
                              id: 'widgets.checkout.bankName',
                            }}
                            autoComplete="organization"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={8}>
                          <CheckoutFormDropdown
                            name="accountType"
                            options={paymentProviderAchTypeMapping[paymentProvider]}
                            emptyOption="Select Account Type"
                            requiredStar
                            htmlSelect
                            label={{
                              id: 'widgets.checkout.checkout.accountType',
                              defaultMessage: 'Account Type',
                            }}
                            customOnChange={event => setFieldValue('accountType', event.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={8}>
                          <CheckoutFormFieldText
                            requiredStar
                            name="routingNumber"
                            label={{
                              defaultMessage: 'Routing Number',
                              id: 'widgets.checkout.routingNumber',
                            }}
                            type="tel"
                            maxLength={9}
                            autoComplete="off"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} md={8}>
                          <CheckoutFormFieldText
                            requiredStar
                            name="accountNumber"
                            label={{
                              defaultMessage: 'Account Number',
                              id: 'widgets.checkout.accountNumber',
                            }}
                            type="tel"
                            autoComplete="off"
                          />
                        </Col>
                        <Col xs={12} md={8}>
                          <CheckoutFormFieldText
                            requiredStar
                            name="accountNumberConfirmation"
                            label={{
                              defaultMessage: 'Confirm Account Number',
                              id: 'widgets.checkout.accountNumberConfirmation',
                            }}
                            type="tel"
                            autoComplete="off"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <Translation
                            id="widgets.checkout.agreeToACH.termsAndConditions"
                            defaultMessage="terms and conditions"
                          >
                            {linkText => (
                              <SMFormFieldToggle
                                type="checkbox"
                                name="agreeToACH"
                                value
                                label={(
                                  <Translation
                                    id="widgets.checkout.agreeToACH"
                                    defaultMessage="By adding this bank account, you agree to the {termsAndConditionsLink}"
                                    values={{
                                      termsAndConditionsLink: <a target="_blank" rel="noreferrer" className="underline" href="https://www.storage-mart.com/ach-terms-conditions">{linkText}</a>,
                                    }}
                                  />
                                )}
                              />
                            )}
                          </Translation>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <SMFormFieldToggle
                            type="checkbox"
                            name="autopayEnroll"
                            value
                            label={(
                              <Translation
                                id="widgets.checkout.autopayEnrollBankAccount"
                                defaultMessage="Enroll in autopay program to withdraw from my bank account on the 1st of every month"
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <h4 className="mb-2">
                            <Translation
                              id="widgets.checkout.billingAddressTitle"
                              defaultMessage="Billing Address"
                            />
                          </h4>
                        </Col>
                      </Row>
                      {useExistingBillingAddress ? (
                        <Row>
                          <Col xs={12}>
                            <div>
                              {formData.tenant.firstName}
                              {' '}
                              {formData.tenant.lastName}
                            </div>
                            <div>
                              {formData.tenant.address1}
                              {formData.tenant.address2 && ', '}
                              {formData.tenant.address2}
                              {', '}
                              {formData.tenant.city}
                              {', '}
                              {formData.tenant.state}
                              {' '}
                              {formData.tenant.postal}
                            </div>
                          </Col>
                          <Col xs={12}>
                            <button type="button" className="pl-0 underline" onClick={useDifferentBillingAddress}>
                              <Translation
                                id="widgets.checkout.useDifferentBillingAddress"
                                defaultMessage="Edit Billing Address"
                              />
                            </button>
                          </Col>
                        </Row>
                      ) : (
                        <>
                          <Row>
                            <Col xs={12} md={4}>
                              <CheckoutFormFieldText
                                requiredStar
                                name="billingFirstName"
                                label={{
                                  defaultMessage: 'First Name',
                                  id: 'widgets.checkout.billingFirstName',
                                }}
                              />
                            </Col>
                            <Col xs={12} md={4}>
                              <CheckoutFormFieldText
                                requiredStar
                                name="billingLastName"
                                label={{
                                  defaultMessage: 'Last Name',
                                  id: 'widgets.checkout.billingLastName',
                                }}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12} md={8}>
                              <CheckoutFormFieldText
                                requiredStar
                                name="billingAddress1"
                                label={{
                                  defaultMessage: 'Address',
                                  id: 'widgets.checkout.address1',
                                }}
                              />
                            </Col>
                            <Col xs={12} md={8}>
                              <CheckoutFormFieldText
                                name="billingAddress2"
                                label={{
                                  defaultMessage: 'Bldg/Apt/Suite (Optional)',
                                  id: 'widgets.checkout.address2',
                                }}
                              />
                            </Col>
                          </Row>
                          <Row>
                            {get(facility, 'address.country') !== GB ? (
                              <Col xs={12} md={4}>
                                <CheckoutFormStateDropdown
                                  label={{
                                    defaultMessage: 'State',
                                    id: 'widgets.checkout.state',
                                  }}
                                  required
                                  name="billingState"
                                  country={get(facility, 'address.country')}
                                  emptyOption={{
                                    defaultMessage: 'Select State',
                                    id: 'widgets.checkout.stateDefault',
                                  }}
                                  onChange={handleChange}
                                />
                              </Col>
                            ) : null}
                            <Col xs={12} md={4}>
                              <CheckoutFormFieldText
                                requiredStar
                                name="billingPostal"
                                label={{
                                  defaultMessage: 'Zip Code',
                                  id: 'widgets.checkout.postal',
                                }}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={12} md={8}>
                              <CheckoutFormFieldText
                                requiredStar
                                name="billingCity"
                                label={{
                                  defaultMessage: 'City',
                                  id: 'widgets.checkout.city',
                                }}
                              />
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
              <div className="border-t border-gray-200" />
              <div>
                <div className="p-4 mb-0">
                  <SMFormFieldToggle
                    type="radio"
                    name="paymentType"
                    value={PAYMENT_TYPES.card}
                    label={(
                      <Translation
                        id="widgets.checkout.creditCard"
                        defaultMessage="Credit Card"
                      />
                    )}
                    className="!mb-0"
                  />
                </div>
                {/* Credit Card Form */}
                {values.paymentType === PAYMENT_TYPES.card && (
                  <div className="px-4 py-2">
                    <Row>
                      <Col xs={12} md={8}>
                        <CheckoutFormFieldCreditCard
                          requiredStar
                          name="creditCardNumber"
                          type="tel"
                          label={{
                            defaultMessage: 'Credit Card Number',
                            id: 'widgets.checkout.creditCardNumber',
                          }}
                          autoComplete="cc-number"
                          validate={value => validateCreditCardNumber(value)}
                          autoCorrect="off"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6} md={4}>
                        <CheckoutFormExpirationDate
                          setFieldValue={setFieldValue}
                          validate={() => (
                            validateExpireDate(
                              values.creditCardExpireMonth,
                              values.creditCardExpireYear,
                              today,
                            )
                          )}
                        />
                      </Col>
                      <Col xs={6} md={3}>
                        <div style={{ position: 'relative' }}>
                          <CheckoutFormFieldText
                            requiredStar
                            name="creditCardSecurityCode"
                            style={{ paddingRight: '44px' }}
                            type="tel"
                            maxLength={4}
                            label={{
                              defaultMessage: 'Security Code',
                              id: 'widgets.checkout.creditCardSecurityCode',
                            }}
                            autoComplete="cc-csc"
                            autoCorrect="off"
                            validate={value => validateCreditCardSecurityCode(value)}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              right: '2px',
                              padding: '12px',
                            }}
                            className={styles.tooltipCCV}
                          >
                            <SMTooltip
                              title={(
                                <IntlProvider locale={intl.locale} messages={intl.messages}>
                                <Translation
                                  id="widgets.checkout.ccvTooltipTitle"
                                  defaultMessage="Locate Your Security Code"
                                />
                                </IntlProvider>
                              )}
                              body={(
                                <IntlProvider locale={intl.locale} messages={intl.messages}>
                                <Translation
                                  id={tooltipCCVTitle}
                                  defaultMessage={tooltipCCVTitleText}
                                />
                                </IntlProvider>
                              )}
                              className={`${styles.tooltip}`}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <SMFormFieldToggle
                          type="checkbox"
                          name="autopayEnroll"
                          value
                          label={(
                            <Translation
                              id="widgets.checkout.autopayEnroll"
                              defaultMessage="Enroll in autopay program to charge my card on the 1st of every month"
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <ApplyPromotion
        appliedPromotion={appliedPromotion}
        checkPromoCodeData={checkPromoCodeData}
        checkingPromoCode={checkingPromoCode}
        formSubmitType={FORM_SUBMIT_TYPES.promo}
        removeAppliedPromotion={removeAppliedPromotion}
        submitting={submitting}
      />
      <Row>
        <Col xs={12}>
          {submitting && <img src={hourglass} className={styles.hourglass} alt="Payment loading" />}
          <CheckoutSubmitButton
            submitLabel={{
              id: 'widgets.checkout.payButton.submit',
              defaultMessage: 'Pay Now and Continue',
            }}
            submitting={submitting}
            submittingLabel={{
              id: 'widgets.checkout.payButton.submitting',
              defaultMessage: 'Paying...',
            }}
            disabled={checkingPromoCode}
            onClick={() => payNowClick('FMI')}
            {...TRACK_CLICK_CHECKOUT_STEP_FIVE_SUBMIT}
            name="submitType"
            value={FORM_SUBMIT_TYPES.payment}
          />
        </Col>
      </Row>
    </form>
  );
};

PaymentForm.propTypes = propTypes;
PaymentForm.defaultProps = defaultProps;

export default withForm({
  mapPropsToValues: (props) => {
    const formattedInitialValues = initialValues;

    formattedInitialValues.billingFirstName = props.formData.tenant.firstName;
    formattedInitialValues.billingLastName = props.formData.tenant.lastName;
    formattedInitialValues.billingAddress1 = props.formData.tenant.address1;
    formattedInitialValues.billingAddress2 = props.formData.tenant.address2;
    formattedInitialValues.billingCity = props.formData.tenant.city;
    formattedInitialValues.billingState = props.formData.tenant.state;
    formattedInitialValues.billingPostal = props.formData.tenant.postal;

    return formattedInitialValues;
  },
  validationSchema,
  handleSubmit: (formPayload, formikBag) => (
    formikBag.props.onSubmit(
      formPayload,
      formikBag.props.currentStep,
      formikBag.props.nextStep,
      formikBag.props.scrollToRef,
    )
  ),
})(PaymentForm);
